import React, { useEffect, useMemo, useState } from 'react';
import * as globals from '../../../../components/GlobalScripts.js';

function toMysqlDatetime(localValue) {
    if (!localValue) return null;
    const v = String(localValue).trim();
    if (!v) return null;
    return v.replace('T', ' ') + ':00';
}

function fromMysqlDatetime(mysqlValue) {
    if (!mysqlValue) return '';
    const s = String(mysqlValue).slice(0, 16).replace(' ', 'T');
    return s;
}

export default function CuponFormModal({ open, initial, onClose, onSubmit }) {
    const isEdit = !!(initial?.id);

    const [form, setForm] = useState({
        id: null,
        codigo: '',
        titulo: '',
        descripcion: '',
        tipo_descuento: 'percent',
        valor: 10,
        moneda: 'USD',
        inicia_at: '',
        expira_at: '',
        max_usos_total: '',
        max_usos_por_cliente: '',
        restricciones: '',
        activo: 1,
        is_visible: 1,
    });

    useEffect(() => {
        if (!open) return;

        if (initial) {
            setForm({
                id: initial.id ?? null,
                codigo: initial.codigo ?? '',
                titulo: initial.titulo ?? '',
                descripcion: initial.descripcion ?? '',
                tipo_descuento: initial.tipo_descuento ?? 'percent',
                valor: initial.valor != null ? Number(initial.valor) : 10,
                moneda: initial.moneda ?? 'USD',
                inicia_at: fromMysqlDatetime(initial.inicia_at),
                expira_at: fromMysqlDatetime(initial.expira_at),
                max_usos_total: initial.max_usos_total ?? '',
                max_usos_por_cliente: initial.max_usos_por_cliente ?? '',
                restricciones: initial.restricciones ?? '',
                activo: Number(initial.activo ?? 1),
                is_visible: Number(initial.is_visible ?? 1),
            });
        } else {
            setForm({
                id: null,
                codigo: '',
                titulo: '',
                descripcion: '',
                tipo_descuento: 'percent',
                valor: 10,
                moneda: 'USD',
                inicia_at: '',
                expira_at: '',
                max_usos_total: '',
                max_usos_por_cliente: '',
                restricciones: '',
                activo: 1,
                is_visible: 1,
            });
        }
    }, [open, initial]);

    const title = useMemo(() => (isEdit ? `Editar cupón` : `Nuevo cupón`), [isEdit]);

    if (!open) return null;

    const close = () => onClose?.();

    const validate = () => {
        const code = String(form.codigo || '').trim();
        if (!code) {
            globals.createAlert({ type: "error", msg_title: "Error", msg: "El código es obligatorio", effect: "opacidad 2s, desplazamiento 2s", time_life: 3500 });
            return false;
        }
        if (code.length > 40) {
            globals.createAlert({ type: "error", msg_title: "Error", msg: "El código no debe exceder 40 caracteres", effect: "opacidad 2s, desplazamiento 2s", time_life: 3500 });
            return false;
        }

        const tipo = String(form.tipo_descuento || '');
        const val = Number(form.valor || 0);

        if (tipo === 'percent') {
            if (!(val > 0 && val <= 80)) {
                globals.createAlert({ type: "error", msg_title: "Error", msg: "El porcentaje debe ser entre 1 y 80", effect: "opacidad 2s, desplazamiento 2s", time_life: 3500 });
                return false;
            }
        } else if (tipo === 'fixed') {
            if (!(val > 0)) {
                globals.createAlert({ type: "error", msg_title: "Error", msg: "El monto fijo debe ser mayor a 0", effect: "opacidad 2s, desplazamiento 2s", time_life: 3500 });
                return false;
            }
        } else {
            globals.createAlert({ type: "error", msg_title: "Error", msg: "Tipo de descuento inválido", effect: "opacidad 2s, desplazamiento 2s", time_life: 3500 });
            return false;
        }

        if (form.inicia_at && form.expira_at && String(form.inicia_at) > String(form.expira_at)) {
            globals.createAlert({ type: "error", msg_title: "Error", msg: "La fecha de inicio no puede ser mayor que la de expiración", effect: "opacidad 2s, desplazamiento 2s", time_life: 3500 });
            return false;
        }

        return true;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            ...(form.id ? { id: form.id } : {}),
            codigo: String(form.codigo || '').trim(),
            titulo: String(form.titulo || '').trim() || null,
            descripcion: String(form.descripcion || '').trim() || null,
            tipo_descuento: form.tipo_descuento,
            valor: Number(form.valor || 0),
            moneda: 'USD',
            inicia_at: toMysqlDatetime(form.inicia_at),
            expira_at: toMysqlDatetime(form.expira_at),
            max_usos_total: form.max_usos_total === '' ? null : Number(form.max_usos_total),
            max_usos_por_cliente: form.max_usos_por_cliente === '' ? null : Number(form.max_usos_por_cliente),
            restricciones: String(form.restricciones || '').trim() || null,
            activo: Number(form.activo) ? 1 : 0,
            is_visible: Number(form.is_visible) ? 1 : 0,
        };

        await onSubmit?.(payload);
    };

    return (
        <div className="cupon-modal__overlay" onMouseDown={close}>
            <div className="cupon-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="cupon-modal__head">
                    <div className="cupon-modal__title">{title}</div>

                    <button className="cupon-iconbtn" type="button" onClick={close} title="Cerrar">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                <form className="cupon-modal__body" onSubmit={submit}>
                    <div className="cupon-grid">
                        <div className="cupon-field">
                            <div className="cupon-label required-label">Código</div>
                            <input
                                className="cupon-input"
                                value={form.codigo}
                                onChange={(e) => setForm((p) => ({ ...p, codigo: e.target.value }))}
                                placeholder="EJ: BIENVENIDA20"
                                maxLength={40}
                                required
                            />
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label required-label">Título</div>
                            <input className="cupon-input" value={form.titulo} onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} placeholder="Ej: Cupón de bienvenida" />
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label required-label">Estado</div>
                            <select className="cupon-select" value={String(form.activo)} onChange={(e) => setForm((p) => ({ ...p, activo: Number(e.target.value) }))}>
                                <option value="1">Activo</option>
                                <option value="0">Inactivo</option>
                            </select>
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label required-label">Visibilidad en la app</div>
                            <select
                                className="cupon-select"
                                value={String(form.is_visible)}
                                onChange={(e) => setForm((p) => ({ ...p, is_visible: Number(e.target.value) }))}
                            >
                                <option value="1">Visible para clientes</option>
                                <option value="0">No visible (campañas internas)</option>
                            </select>
                        </div>

                        <div className="cupon-field cupon-field--full">
                            <div className="cupon-label required-label">Descripción</div>
                            <input className="cupon-input" value={form.descripcion} onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))} placeholder="Ej: Aplica en renovación, por tiempo limitado." />
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label required-label">Tipo</div>
                            <select className="cupon-select" value={form.tipo_descuento} onChange={(e) => setForm((p) => ({ ...p, tipo_descuento: e.target.value }))}>
                                <option value="percent">% Porcentaje</option>
                                {/*<option value="fixed">$ Monto fijo</option>*/}
                            </select>
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label required-label">Valor</div>
                            <input
                                className="cupon-input"
                                type="number"
                                min={form.tipo_descuento === 'percent' ? 1 : 0.01}
                                max={form.tipo_descuento === 'percent' ? 80 : undefined}
                                step={form.tipo_descuento === 'percent' ? 1 : 0.01}
                                value={String(form.valor)}
                                onChange={(e) => setForm((p) => ({ ...p, valor: e.target.value }))}
                            />
                            <div className="cupon-hint">
                                {form.tipo_descuento === 'percent' ? 'Máximo permitido: 80%' : 'Monto fijo en USD.'}
                            </div>
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label">Inicia</div>
                            <input className="cupon-input" type="datetime-local" value={form.inicia_at} onChange={(e) => setForm((p) => ({ ...p, inicia_at: e.target.value }))} />
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label">Expira</div>
                            <input className="cupon-input" type="datetime-local" value={form.expira_at} onChange={(e) => setForm((p) => ({ ...p, expira_at: e.target.value }))} />
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label">Máx. usos total</div>
                            <input className="cupon-input" type="number" min={1} value={form.max_usos_total} onChange={(e) => setForm((p) => ({ ...p, max_usos_total: e.target.value }))} />
                        </div>

                        <div className="cupon-field">
                            <div className="cupon-label">Máx. usos por cliente</div>
                            <input className="cupon-input" type="number" min={1} value={form.max_usos_por_cliente} onChange={(e) => setForm((p) => ({ ...p, max_usos_por_cliente: e.target.value }))} />
                        </div>

                        {/*<div className="cupon-field cupon-field--full">
                            <div className="cupon-label">Restricciones (opcional)</div>
                            <textarea
                                className="cupon-textarea"
                                rows={3}
                                value={form.restricciones}
                                onChange={(e) => setForm((p) => ({ ...p, restricciones: e.target.value }))}
                                placeholder=""
                            />
                        </div>*/}
                    </div>

                    <div className="cupon-modal__foot">
                        <button className="cupon-btn cupon-btn--ghost" type="button" onClick={close}>
                            Cancelar
                        </button>
                        <button className="cupon-btn cupon-btn--primary" type="submit">
                            <span className="cupon-btn__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24"><path d="M5 12l4 4L19 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </span>
                            Guardar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
